import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addRejectionReason,
  resolveRejectionReason,
  removeRejectionReason,
} from '../api/potsApi';
import { potKeys } from './potKeys';

/**
 * Mutation hook for adding a rejection reason to a rejected pot.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export function useAddRejectionReason() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      potId,
      reason,
    }: {
      potId: string;
      reason: string;
    }) => {
      return await addRejectionReason(potId, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: potKeys.all });
      queryClient.invalidateQueries({ queryKey: potKeys.details() });
    },
  });
}

/**
 * Mutation hook for marking a rejection reason as resolved.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export function useResolveRejectionReason() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      potId,
      reasonId,
    }: {
      potId: string;
      reasonId: string;
    }) => {
      return await resolveRejectionReason(potId, reasonId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: potKeys.all });
      queryClient.invalidateQueries({ queryKey: potKeys.details() });
    },
  });
}

/**
 * Mutation hook for removing (soft deleting) a rejection reason.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export function useRemoveRejectionReason() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      potId,
      reasonId,
    }: {
      potId: string;
      reasonId: string;
    }) => {
      return await removeRejectionReason(potId, reasonId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: potKeys.all });
      queryClient.invalidateQueries({ queryKey: potKeys.details() });
    },
  });
}
